<script setup lang="ts">
import { useIsMutating, useQuery } from '@tanstack/vue-query'
import { creditNoteQueries } from '@/features/invoices/queries'
import CardContainer from '@/components/Card/card-container.vue'
import CardHeader from '@/components/Card/card-header.vue'
import InputSelect from '@/components/input-select.vue'
import { formatToMainCurrency } from '@/helpers/formatting'
import CardContent from '@/components/Card/card-content.vue'
import InvoiceSkeletonPlaceholderText from '@/features/invoices/components/partials/InvoiceSkeletonPlaceholderText.vue'
import { computed, ref, watchEffect } from 'vue'
import { objectWithKeys } from '@/helpers'
import InputNumber from '@/components/input-number.vue'
import ContentLoader from '@/components/content-loader.vue'
import { InvoiceStatusNumeric } from '@/features/invoices/constants'
import { watchEffectUntilStopped } from '@/shared/composables/watchEffectUntilStopped'

const emit = defineEmits<{ 'update:allocated': [{ error: boolean, amountInCents: number }] }>()
const props = defineProps<{ invoiceId: number, invoiceStatus: InvoiceStatusNumeric }>()
const { data: applicableCreditNotes, isFetching } = useQuery(creditNoteQueries.allocatable(props.invoiceId))
const selectedCreditNoteIds = ref<number[]>([])
const allocatedAmountsInCents = ref<{ [k: number]: number|undefined }>({})
const pendingMutationCount = useIsMutating()

watchEffectUntilStopped(
  () => {
    // Sometimes the mutation to update the credit notes is still running when this component is loaded (usually when
    // updating + reloading the invoice finishes before the credit notes are finished allocating). This is why we check
    // if there are any pending mutations.
    if (applicableCreditNotes.value === undefined || isFetching.value || pendingMutationCount.value > 0) {
      return
    }

    // Populate the initial value of the allocated amounts.
    allocatedAmountsInCents.value = Object.fromEntries(
      applicableCreditNotes.value.flatMap(
        creditNote => creditNote.creditNoteItems.flatMap(
          item => item.allocations.flatMap(x => ({ ...x, itemId: item.id }))
        )
      ).filter(
        x => x.invoiceId === props.invoiceId
      ).map(
        x => [x.itemId, x.allocatedAmountInCents]
      )
    )

    // Populate the initial selected credit notes.
    selectedCreditNoteIds.value = applicableCreditNotes.value
      .filter(
        creditNote => creditNote.creditNoteItems.some(
          item => item.allocations.some(alloc => alloc.invoiceId === props.invoiceId)
        )
      )
      .map(
        x => x.id
      )

    return true
  }
)

const isEditable = computed(() => props.invoiceStatus === InvoiceStatusNumeric.Draft)

const availableAllocations = computed(
  () => (
    applicableCreditNotes.value?.filter(
      x => selectedCreditNoteIds.value.includes(x.id)
    )
    .map(
      creditNote => creditNote.creditNoteItems.map(
        creditNoteItem => ({
          ...objectWithKeys(creditNoteItem, ['id', 'description', 'quantityInKWh', 'tariffInCents']),
          creditNote: {
            id: creditNote.id,
            name: creditNote.creditNoteReference,
            remaining: creditNote.remainingAmountInCents,
          },
        })
      )
    ).flat(2) ?? []
  )
)

const remainingAmountsInCentsByCreditNote = computed(
  () => (
    !isEditable.value
      ? { } as Record<number, number>
      : availableAllocations.value.reduce(
      (acc, item) => {
        const creditNoteId = item.creditNote.id

        if (!(creditNoteId in acc)) {
          acc[creditNoteId] = item.creditNote.remaining
        }
        acc[creditNoteId] -= allocatedAmountsInCents.value[item.id] ?? 0

        return acc
      },
      { } as Record<number, number>
    )
  )
)

function updateAllocatedAmount(id: number, amount?: number) {
  if (typeof amount === 'number' && !isNaN(amount) && amount > 0) {
    allocatedAmountsInCents.value[id] = amount * 100
  } else {
    allocatedAmountsInCents.value[id] = undefined
  }
}

const subTotalInCents = computed(
  () => availableAllocations.value.map(
    x => allocatedAmountsInCents.value[x.id] ?? 0
  ).reduce(
    (acc, value) => acc + value,
    0
  )
)

// Watch the remaining amounts, and update the error if anything is exceeded.
watchEffect(
  () => {
    const error = Object.values(remainingAmountsInCentsByCreditNote.value).some(x => x < 0)

    emit('update:allocated', { error, amountInCents: subTotalInCents.value })
  }
)

defineExpose({
  allocations() {
    return availableAllocations.value.map(
      x => {
        const amount = allocatedAmountsInCents.value[x.id]

        return typeof amount === 'number' && !isNaN(amount)
          ? { id: x.id, amountInCents: amount }
          : undefined
      }
    ).filter(
      x => x !== undefined
    )
  },
  clearAllocations() {
    allocatedAmountsInCents.value = { }
  }
})
</script>

<template>
  <CardContainer>
    <CardHeader :size="isEditable ? 'small' : 'normal'">
      <div class="d-flex align-items-center">
        <span class="text-nowrap">CREDIT NOTE</span>

        <InputSelect v-if="isEditable"
          class="ms-3"
          multiple
          v-model:selected="selectedCreditNoteIds"
          :items="(applicableCreditNotes ?? []).map(x => ({ value: x.id, text: `${x.creditNoteReference} | ${formatToMainCurrency(x.remainingAmountInCents / 100)} remaining`}))"
          item-text="text"
          item-value="value" />
      </div>
    </CardHeader>
    <CardContent>
      <table class="table table-no-bottom-border">
        <thead>
          <tr>
            <th>Credit Note No.</th>
            <th>Description</th>
            <th class="text-end">kWh</th>
            <th class="text-end">Tariff (R/kWh)</th>
            <th class="text-end">Subtotal (R)</th>
            <th class="text-end">Credit Applied (R)</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="availableAllocations.length > 0">
            <tr v-for="item in availableAllocations" :key="item.id">
              <td>{{ item.creditNote.name }}</td>
              <td>{{ item.description }}</td>
              <td class="text-end">{{ item.quantityInKWh }}</td>
              <td class="text-end">{{ formatToMainCurrency(item.tariffInCents / 100, { showSymbol: false }) }}</td>
              <td class="text-end">{{ formatToMainCurrency(item.quantityInKWh * item.tariffInCents / 100, { showSymbol: false }) }}</td>
              <td class="text-end ps-4" style="width: 15%">
                <InputNumber v-if="isEditable" :value="allocatedAmountsInCents[item.id] ? allocatedAmountsInCents[item.id]! / 100 : undefined"
                             align="right"
                             @update:value="updateAllocatedAmount(item.id, $event)"
                             placeholder="0.00"
                             error-variant="tooltip"
                             :error="
                               remainingAmountsInCentsByCreditNote[item.creditNote.id] < 0
                                 ? `You have allocated more than is remaining on this credit note.\nYou have allocated ${formatToMainCurrency(Math.abs(remainingAmountsInCentsByCreditNote[item.creditNote.id] / 100))} more than is available.`
                                 : undefined
                             "
                             class="text-end">
                  <template #prefix><span class="text-muted">R</span></template>
                </InputNumber>
                <span v-else>{{ formatToMainCurrency(allocatedAmountsInCents[item.id] ? allocatedAmountsInCents[item.id]! / 100 : undefined, { showSymbol: false }) ?? '-' }}</span>
              </td>
            </tr>
          </template>
          <tr v-else-if="isFetching || pendingMutationCount > 0">
            <td colspan="6">
              <ContentLoader loading variant="table" :cols="6" :rows="1" :table-header="false" />
            </td>
          </tr>
          <tr v-else>
            <td><InvoiceSkeletonPlaceholderText /></td>
            <td><InvoiceSkeletonPlaceholderText /></td>
            <td class="text-end"><InvoiceSkeletonPlaceholderText /></td>
            <td class="text-end"><InvoiceSkeletonPlaceholderText /></td>
            <td class="text-end"><InvoiceSkeletonPlaceholderText /></td>
            <td class="text-end"><InvoiceSkeletonPlaceholderText /></td>
          </tr>
        </tbody>
      </table>

      <div :class="$style.summary">
        <div class="text-end">Subtotal</div>
        <div>{{ formatToMainCurrency(subTotalInCents / 100) }}</div>
      </div>
    </CardContent>
  </CardContainer>
</template>

<style module lang="scss">
@import "@/assets/variables";

.summary {
  background: #eee;
  font-weight: 700;
  padding: 20px 20px 20px 64.5%;
  border-bottom-left-radius: #{$card-border-radius};
  border-bottom-right-radius: #{$card-border-radius};
  display: grid;
  grid-template-columns: 167px max-content;
  column-gap: 78px;
  margin: #{$card-spacer-y} #{-1 * $card-spacer-x} #{-1 * $card-spacer-y};
}
</style>
